/** What a `delegate` call's card shows, read off the tool result (ADR-0017, ADR-0062). */
export type DelegateStatus = "done" | "timeout" | "stopped" | "failed";

/** An external coding job's reported model, effort and optional runtime metadata. */
export interface CodingWorkerRun {
	model: string;
	effort: string;
	session?: string;
	turns?: number;
	cost?: number;
}

export interface DelegateResult {
	profile: string;
	/** A job that can be continued: its first job's id and this run of it (ADR-0084). */
	job?: string;
	run?: number;
	/** The session a pi job ran in; an external coding job has none to open. */
	sessionId?: string;
	toolCalls?: number;
	/** Historical Claude headers retain their original identity. */
	claudeCode?: CodingWorkerRun;
	codingWorker?: CodingWorkerRun & {engine:"codex-exec"};
	status: DelegateStatus;
	/** The body under the header: the callee's reply, or what stopped it. */
	reply: string;
}

const HEADER = /^delegated to (\S+)(?: · job (\S+) · run (\d+) of \d+)? · session (\S+) · (\d+) tool calls?\n\n([\s\S]*)$/;
const QUEUED = /background job ([0-9a-f-]{36})/;
const CLAUDE_CODE_HEADER = /^delegated to (\S+) · Claude Code · model (\S+) · effort (\S+)(?: · claude session (\S+))?(?: · (\d+) turns?)?(?: · \$(\d+(?:\.\d+)?))?\n\n([\s\S]*)$/;
const CODEX_HEADER = /^delegated to (\S+) · Codex · model (\S+) · effort (\S+)(?: · codex thread (\S+))?(?: · (\d+) turns?)?(?: · \$(\d+(?:\.\d+)?))?\n\n([\s\S]*)$/;

function statusOf(reply: string): DelegateStatus {
	return reply.startsWith("the job hit the")
		? "timeout"
		: reply.startsWith("the job was stopped")
			? "stopped"
			: reply.startsWith("the job failed")
				? "failed"
				: "done";
}

/** Either header line the tool writes, then the body's prefix; undefined for any other text. */
export function readDelegateResult(text: string): DelegateResult | undefined {
	const m = HEADER.exec(text);
	if (m) return { profile: m[1]!, ...(m[2] ? { job: m[2], run: Number(m[3]) } : {}), sessionId: m[4]!, toolCalls: Number(m[5]), status: statusOf(m[6]!), reply: m[6]! };
	const codex=CODEX_HEADER.exec(text);
	const c = codex ?? CLAUDE_CODE_HEADER.exec(text);
	if (!c) return undefined;
	const run: CodingWorkerRun = { model: c[2]!, effort: c[3]! };
	if (c[4]) run.session = c[4];
	if (c[5]) run.turns = Number(c[5]);
	if (c[6]) run.cost = Number(c[6]);
	return { profile: c[1]!, ...(codex ? {codingWorker:{...run,engine:"codex-exec" as const}} : {claudeCode:run}), status: statusOf(c[7]!), reply: c[7]! };
}

/** Who the card says got the job: the call's `agent`, an older call's `profile`, else who its result names. */
export function cardTarget(args: unknown, read?: DelegateResult): string {
	const a = (args ?? {}) as { agent?: string; profile?: string };
	return a.agent ?? a.profile ?? read?.profile ?? "?";
}

/** The background job a call's reply says it queued: a first call's or a later run's. */
export function queuedJob(text: string): string | undefined {
	return QUEUED.exec(text)?.[1];
}

/**
 * The session the card opens: the result's once it is in, else the one announced while the job runs, else the
 * session of the background job the call queued.
 */
export function openableSession(read: DelegateResult | undefined, announced: string | undefined, jobSession?: string): string | undefined {
	return read ? read.sessionId : (announced ?? jobSession);
}
